
import { Button, CircularProgress, Link, Paper as MaterialPaper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@material-ui/core";
import React, { Fragment } from "react";
import ExaminerContainer from "../ExaminerContainer";
import { Paper } from '../../../interfaces/shared/Paper'
import SavingIndicator from "../../../components/SavingIndicator";
import SaveCancelButtons from "../../../components/SaveCancelButtons";
import { Question } from "../../../interfaces/shared/Question";
import { Cancel, Delete } from "@material-ui/icons";
import AddQuestionToPaperDialog from "./AddQuestionToPaperDialog";
import request, { isLoggedIn } from "../../../api";
import { Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import { UnsavedChangesHandler } from "../../../utils/unsavedChanges";
import UnsavedChangesDialog from "../../../components/UnsavedChangesDialog";

interface Props {
  id: string
}

interface State {
    paper:Paper|null,
    saving:boolean,
    showAddQuestionDialog:boolean
    hasChanges: boolean
    showUnsavedDialog: boolean
}

class ExaminerEditPaperPage extends React.Component<Props & RouteComponentProps, State> {

  private unsavedChanges = new UnsavedChangesHandler()

  constructor(props: Props & RouteComponentProps) {

      super(props)

      this.state = {
          paper:null,
          saving:false,
          showAddQuestionDialog:false,
          hasChanges: false,
          showUnsavedDialog: false
      }

      this.loadPaper()
  }

  componentDidUpdate(prevProps: Props & RouteComponentProps, prevState: State) {
    if (prevState.hasChanges !== this.state.hasChanges) {
      this.unsavedChanges.setup(this.props.history, this.state.hasChanges, () => {
        this.setState(prev => ({ ...prev, showUnsavedDialog: true }))
      })
    }
  }

  componentWillUnmount() {
    this.unsavedChanges.cleanup()
  }

  render() {

    let paper:Paper|null = this.state.paper
    let saving = this.state.saving

    if(!isLoggedIn('examiner')) {
        return <Redirect to='/login' />
    }


    if(!paper) {
        return <ExaminerContainer heading={"Loading..."}>Loading...</ExaminerContainer>
    }

      return (
          <ExaminerContainer heading={"Edit Paper"}>
              <UnsavedChangesDialog
                open={this.state.showUnsavedDialog}
                onYes={this.handleUnsavedYes}
                onNo={this.handleUnsavedNo}
              />
              {
                  this.state.showAddQuestionDialog &&
                  <AddQuestionToPaperDialog paper={paper} onAdd={this.onAddQuestionToPaper} onClose={this.onCloseAddQuestionDialog} />
              }
              <SavingIndicator saving={saving}/>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Paper Title"
                    fullWidth
                    defaultValue={paper.title}
                    onChange={this.handlePaperTitleChange}
                />
                <TextField
                    autoFocus
                    margin="dense"
                    id="duration"
                    label="Duration (minutes)"
                    fullWidth
                    defaultValue={paper.duration}
                    onChange={this.handlePaperDurationChange}
                />
                <SaveCancelButtons
                    onSave={this.handleSave}
                    onCancel={this.handleCancel}
                    saveLoading={saving}
                    saveDisabled={!this.state.hasChanges}
                />
                <br />
                <br />
                <h2>Questions</h2>
             <TableContainer component={MaterialPaper}>
            <Table aria-label="simple table">
                <TableBody>
                    {paper.questions.map((question:Question, i:number) => (
                        <TableRow>
                            <TableCell component="th" scope="option">
                                {!question.isArchived && <Fragment>
                                    {i > 0 && <Button onClick={() => this.moveQuestion(i, i - 1)}>▲</Button>}
                                    {i < paper!.questions.length - 1 && <Button onClick={() => this.moveQuestion(i, i + 1)}>▼</Button>}
                                    <Button onClick={() => this.removeQuestion(i)}><Cancel /></Button>
                                </Fragment>}
                            </TableCell>
                            <TableCell align="left">
                                <Link href={'/examiner/questions/' + question.id}>{question.description}</Link>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell colSpan={3}>
                            <Button color="primary" variant="outlined" onClick={this.addQuestion}>
                                + Add Question
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
          </ExaminerContainer>
      )
  }


    handlePaperTitleChange = async (ev: any) => {

        let paper = this.state.paper as Paper

        let newPaper:Paper = { ...paper, title: ev.target.value }

        await this.setState(prevState => ({ ...prevState, paper: newPaper, hasChanges: true }))
    }

    handlePaperDurationChange = async (ev: any) => {

        let paper = this.state.paper as Paper

        let newPaper:Paper = { ...paper, duration: parseInt(ev.target.value) }

        await this.setState(prevState => ({ ...prevState, paper: newPaper, hasChanges: true }))
    }

    private async loadPaper() {

        let paper = await (await request('/api/examiner/papers/' + this.props.id)).json()

        this.setState(prevState => ({ ...prevState, paper }))
    }


    async save() {
        await request('/api/examiner/papers/' + this.props.id, {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(this.state.paper)
        })
        await this.setState(prevState => ({ ...prevState, saving: false, hasChanges: false }))
    }

    handleSave = async () => {
        await this.setState(prevState => ({ ...prevState, saving: true }))
        await this.save()
    }

    handleCancel = async () => {
        // Reload the paper data to discard changes
        await this.loadPaper()
        await this.setState(prevState => ({ ...prevState, hasChanges: false }))
    }

    addQuestion = () => {
        this.setState(prevState => ({ ...prevState, showAddQuestionDialog: true }))
    }

    handleUnsavedYes = () => {
      this.setState(prev => ({ ...prev, showUnsavedDialog: false }))
      this.unsavedChanges.confirm(this.props.history)
    }

    handleUnsavedNo = () => {
      this.setState(prev => ({ ...prev, showUnsavedDialog: false }))
      this.unsavedChanges.cancel()
    }

    onAddQuestionToPaper = async (question:Question) => {
        let body = {
            paper: this.props.id
        }
        await request(`/api/examiner/questions/${question.id}/paper`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify(body)
        })
        await this.setState(prevState => ({ ...prevState, showAddQuestionDialog: false }))
        this.loadPaper()
    }

    onCloseAddQuestionDialog = () => {
        this.setState(prevState => ({ ...prevState, showAddQuestionDialog: false }))
    }

    async moveQuestion(n: number, n2: number) {

        let paper = this.state.paper as Paper

        let newPaper:Paper = { ...paper }

        newPaper.questions.splice(n2, 0, newPaper.questions.splice(n, 1)[0])

        for(let n = 0; n < newPaper.questions.length; ++ n) {
            newPaper.questions[n].order = n
        }

        await this.setState(prevState => ({ ...prevState, paper: newPaper, hasChanges: true }))
    }

    async removeQuestion(n: number) {

        let paper = this.state.paper as Paper

        let newPaper:Paper = { ...paper }

        newPaper.questions.splice(n, 1)

        await this.setState(prevState => ({ ...prevState, paper: newPaper, hasChanges: true }))
    }


}

export default withRouter(ExaminerEditPaperPage)






